import { ref, onUnmounted } from "vue";
import { conversationResponseApi } from "../api";
import { ResponseState } from "../types";
// Centralized polling manager with single poller
class ConversationPollingManager {
    responseRefs = new Map();
    pollingInterval = null;
    intervalMs = 5000;
    register(conversationId, responseRef) {
        this.responseRefs.set(conversationId, responseRef);
        // Start global polling if we have responses that need summaries
        this.checkAndStartPolling();
    }
    unregister(conversationId) {
        console.log(`[PollingManager] Unregistering response for conversationId: ${conversationId}`);
        this.responseRefs.delete(conversationId);
        // Stop polling if no more responses need summaries
        this.checkAndStopPolling();
    }
    checkAndStartPolling() {
        // Don't start if already polling
        if (this.pollingInterval)
            return;
        // Check if any registered responses need summaries (complete but no summary)
        const needsPolling = Array.from(this.responseRefs.values()).some((responseRef) => responseRef.value.state === ResponseState.DRAFT || !responseRef.value.summary?.content);
        if (needsPolling) {
            console.log(`[PollingManager] Starting global polling, interval: ${this.intervalMs}ms`);
            this.startPolling();
        }
    }
    checkAndStopPolling() {
        // Stop if no responses registered or all complete responses have summaries
        const needsPolling = Array.from(this.responseRefs.values()).some((responseRef) => responseRef.value.state === ResponseState.DRAFT || !responseRef.value.summary?.content);
        if (!needsPolling && this.pollingInterval) {
            console.log(`[PollingManager] All responses have summaries, stopping global polling`);
            this.stopPolling();
        }
    }
    startPolling() {
        this.pollingInterval = setInterval(() => {
            this.pollAllResponses();
        }, this.intervalMs);
        // Initial poll
        this.pollAllResponses();
    }
    stopPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }
    async pollAllResponses() {
        console.log(`[PollingManager] Polling ${this.responseRefs.size} registered responses`);
        // Get all conversation IDs that need summaries (complete but no summary)
        const conversationIds = Array.from(this.responseRefs.entries())
            .filter(([, responseRef]) => responseRef.value.state === ResponseState.DRAFT || !responseRef.value.summary?.content)
            .map(([conversationId]) => conversationId);
        if (conversationIds.length === 0) {
            console.log(`[PollingManager] No responses need summaries, stopping polling`);
            this.stopPolling();
            return;
        }
        // Fetch all responses in parallel
        const fetchPromises = conversationIds.map((conversationId) => this.fetchAndUpdate(conversationId));
        await Promise.allSettled(fetchPromises);
        // Check if we should stop polling
        this.checkAndStopPolling();
    }
    async fetchAndUpdate(conversationId) {
        try {
            const response = await conversationResponseApi.getConversationResponse(conversationId);
            const responseRef = this.responseRefs.get(conversationId);
            if (responseRef) {
                console.log(`[PollingManager] Updating response for conversationId: ${conversationId}`, response);
                responseRef.value = response;
                if (response.summary?.content) {
                    console.log(`[PollingManager] Summary received for conversationId: ${conversationId}`);
                }
            }
            return response;
        }
        catch (error) {
            console.error(`[PollingManager] Error fetching conversationId: ${conversationId}`, error);
            return null;
        }
    }
    // Cleanup all intervals
    cleanup() {
        console.log(`[PollingManager] Cleaning up polling`);
        this.stopPolling();
        this.responseRefs.clear();
    }
}
// Global singleton manager
const pollingManager = new ConversationPollingManager();
// Cleanup on app unmount (if running in browser)
if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", () => pollingManager.cleanup());
}
export function useConversationResponse(initialResponse) {
    // Local reactive ref initialized with prop
    const response = ref(initialResponse);
    // Register with polling manager
    pollingManager.register(initialResponse.conversationId, response);
    // Cleanup on component unmount
    onUnmounted(() => {
        pollingManager.unregister(initialResponse.conversationId);
    });
    const regenerateSummary = async () => {
        try {
            // Call API to regenerate summary
            await conversationResponseApi.regenerateSummary(initialResponse.conversationId);
            // Clear the current summary and re-register for polling
            pollingManager.unregister(initialResponse.conversationId);
            response.value.summary = undefined;
            setTimeout(() => {
                pollingManager.register(initialResponse.conversationId, response);
            }, 3000); // Give the backend a few seconds to start processing
        }
        catch (error) {
            console.error(`[useConversationResponse] Error regenerating summary for conversationId: ${initialResponse.conversationId}`, error);
        }
    };
    return {
        response,
        regenerateSummary
    };
}
